import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, InputNumber, Row, Col, message } from "antd";
import { educationAssetService } from "@iso/services/educationAsset.service";
import { userService } from "@iso/services/user.service";
import { educationTypes } from "@iso/constants/educationTypes";
import moment from "moment";
import { useSelector } from "react-redux";

const { Option } = Select;
const { TextArea } = Input;

const EducationAssetModal = ({ visible, onClose, onSuccess, editingAsset, propertyId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [primaryIsExternal, setPrimaryIsExternal] = useState(false);
  const [secondaryHasExternal, setSecondaryHasExternal] = useState(false);
  const currentUser = useSelector((state) => state.user.user);

  useEffect(() => {
    // Load users for trainer selection
    if (propertyId) {
      userService.getUserByProperty(propertyId).then((res) => {
        if (res && res.code === "0000") {
          setUsers(res.users || []);
        }
      });
    }
  }, [propertyId]);

  useEffect(() => {
    if (editingAsset) {
      const primaryExt = editingAsset.primary_external_trainer || null;
      const secExtList = editingAsset.secondary_external_trainers || [];
      const secExt = secExtList.length > 0 ? secExtList[0] : null;
      setPrimaryIsExternal(!!primaryExt);
      setSecondaryHasExternal(!!secExt);
      form.setFieldsValue({
        name: editingAsset.name,
        type: editingAsset.type,
        description: editingAsset.description,
        primary_trainer_id: primaryExt ? 'external' : editingAsset.primary_trainer_id,
        primary_ext_name: primaryExt ? primaryExt.name : undefined,
        primary_ext_email: primaryExt ? primaryExt.email : undefined,
        primary_ext_company: primaryExt ? primaryExt.company : undefined,
        primary_ext_phone: primaryExt ? primaryExt.phone : undefined,
        secondary_trainer_ids: editingAsset.secondary_trainer_ids
          ? [...(editingAsset.secondary_trainer_ids), ...(secExt ? ['external'] : [])]
          : (secExt ? ['external'] : []),
        sec_ext_name: secExt ? secExt.name : undefined,
        sec_ext_email: secExt ? secExt.email : undefined,
        sec_ext_company: secExt ? secExt.company : undefined,
        sec_ext_phone: secExt ? secExt.phone : undefined,
        active_date: editingAsset.active_date ? moment(editingAsset.active_date) : null,
        expiry_date: editingAsset.expiry_date ? moment(editingAsset.expiry_date) : null,
        validity_months: editingAsset.validity_months || 12,
      });
    } else {
      setPrimaryIsExternal(false);
      setSecondaryHasExternal(false);
      form.resetFields();
      form.setFieldsValue({
        active_date: moment(),
        validity_months: 12,
      });
    }
  }, [editingAsset, form, visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const isPrimaryExternal = values.primary_trainer_id === 'external';
      const secIds = (values.secondary_trainer_ids || []).filter(id => id !== 'external');
      const hasSecExt = (values.secondary_trainer_ids || []).includes('external');

      const data = {
        ...values,
        property_id: propertyId,
        active_date: values.active_date ? values.active_date.format("YYYY-MM-DD") : null,
        expiry_date: values.expiry_date ? values.expiry_date.format("YYYY-MM-DD") : null,
        primary_trainer_id: isPrimaryExternal ? null : values.primary_trainer_id,
        primary_external_trainer: isPrimaryExternal ? {
          name: values.primary_ext_name,
          email: values.primary_ext_email,
          company: values.primary_ext_company || null,
          phone: values.primary_ext_phone,
        } : null,
        secondary_trainer_ids: secIds,
        // Per customer feedback: each external trainer slot accepts at most one
        // entry. Primary occupies its own column; the secondary slot is a
        // single-element array.
        secondary_external_trainers: hasSecExt ? [{
          name: values.sec_ext_name,
          email: values.sec_ext_email,
          company: values.sec_ext_company || null,
          phone: values.sec_ext_phone,
        }] : [],
      };
      delete data.primary_ext_name;
      delete data.primary_ext_email;
      delete data.primary_ext_company;
      delete data.primary_ext_phone;
      delete data.sec_ext_name;
      delete data.sec_ext_email;
      delete data.sec_ext_company;
      delete data.sec_ext_phone;

      const res = await educationAssetService.storeOrUpdate(data, editingAsset?.id);

      if (res && res.code === "0000") {
        message.success(editingAsset ? "Education asset updated successfully" : "Education asset created successfully");
        onSuccess();
      } else {
        message.error(res?.message || "Failed to save education asset");
      }
      setLoading(false);
    } catch (error) {
      console.error("Validation failed:", error);
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editingAsset ? "Edit Education Asset" : "Add Education Asset"}
      visible={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      okText={editingAsset ? "Update" : "Create"}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, whitespace: true, message: "Name is required" }]}
        >
          <Input placeholder="Enter asset name" />
        </Form.Item>

        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true, message: "Type is required" }]}
        >
          <Select placeholder="Select type">
            {educationTypes.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Enter description" />
        </Form.Item>

        <Form.Item
          name="primary_trainer_id"
          label="Primary Trainer"
          rules={[{ required: true, message: "Primary trainer is required" }]}
        >
          <Select
            placeholder="Select primary trainer"
            showSearch
            allowClear
            filterOption={(input, option) =>
              String(option.children).toLowerCase().includes(input.toLowerCase())
            }
            onChange={(val) => setPrimaryIsExternal(val === 'external')}
          >
            <Option key="external" value="external" style={{ fontStyle: 'italic' }}>External Trainer</Option>
            {users.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {primaryIsExternal && (
          <div style={{ background: '#f9f9f9', border: '1px solid #e8e8e8', borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="primary_ext_name" label="Full Name" rules={[{ required: true, whitespace: true, message: 'Full name is required' }]}>
                  <Input placeholder="Trainer full name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="primary_ext_email" label="Email" rules={[{ required: true, whitespace: true, type: 'email', message: 'Valid email is required' }]}>
                  <Input placeholder="trainer@example.com" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="primary_ext_phone" label="Phone Number" rules={[{ required: true, whitespace: true, message: 'Phone number is required' }]}>
                  <Input placeholder="+61 400 000 000" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="primary_ext_company" label="Company Name">
                  <Input placeholder="Company (optional)" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        <Form.Item name="secondary_trainer_ids" label="Secondary Trainers">
          <Select
            mode="multiple"
            placeholder="Select secondary trainers"
            showSearch
            filterOption={(input, option) =>
              String(option.children).toLowerCase().includes(input.toLowerCase())
            }
            onChange={(vals) => setSecondaryHasExternal(vals.includes('external'))}
          >
            <Option key="external" value="external" style={{ fontStyle: 'italic' }}>External Trainer</Option>
            {users.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {secondaryHasExternal && (
          <div style={{ background: '#f9f9f9', border: '1px solid #e8e8e8', borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="sec_ext_name" label="Full Name" rules={[{ required: true, whitespace: true, message: 'Full name is required' }]}>
                  <Input placeholder="Trainer full name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sec_ext_email" label="Email" rules={[{ required: true, whitespace: true, type: 'email', message: 'Valid email is required' }]}>
                  <Input placeholder="trainer@example.com" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="sec_ext_phone" label="Phone Number" rules={[{ required: true, whitespace: true, message: 'Phone number is required' }]}>
                  <Input placeholder="+61 400 000 000" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sec_ext_company" label="Company Name">
                  <Input placeholder="Company (optional)" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        <Form.Item name="active_date" label="Active Date">
          <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="validity_months" label="Validity (Months)">
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="expiry_date" label="Expiry Date">
          <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EducationAssetModal;
